import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import DOMPurify from 'dompurify';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { LogOut, Users, ClipboardList, Award, BookOpen, ClipboardCheck } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusColor = (s) => {
  if (s === 'green') return 'bg-green-100 text-green-700';
  if (s === 'yellow') return 'bg-amber-100 text-amber-700';
  if (s === 'red') return 'bg-red-100 text-red-700';
  return 'bg-slate-100 text-slate-600';
};
const statusLabel = (s) => ({ green: 'Постигната', yellow: 'В процес', red: 'Незапочната' }[s] || s || '-');

export default function MonInspectorDashboard() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [selectedStudent, setSelectedStudent] = useState('');
  const [attendance, setAttendance] = useState([]);
  const [competencies, setCompetencies] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchBase = useCallback(async () => {
    try {
      const [s, l] = await Promise.all([
        axios.get(`${API_URL}/mon/students`, { headers }),
        axios.get(`${API_URL}/mon/lessons`, { headers }),
      ]);
      setStudents(s.data);
      setLessons(l.data);
    } catch (e) {
      toast.error('Грешка при зареждане на данните');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { fetchBase(); }, [fetchBase]);

  const loadStudentDetail = async (id) => {
    setSelectedStudent(id);
    if (!id) { setAttendance([]); setCompetencies([]); return; }
    try {
      const [a, c] = await Promise.all([
        axios.get(`${API_URL}/mon/student/${id}/attendance`, { headers }),
        axios.get(`${API_URL}/mon/student/${id}/competencies`, { headers }),
      ]);
      setAttendance(a.data);
      setCompetencies(c.data);
    } catch (e) {
      toast.error('Грешка при зареждане на ученика');
    }
  };

  const handleLogout = () => { logout(); navigate('/'); };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen text-lg">Зареждане...</div>;
  }

  const StudentPicker = () => (
    <Select value={selectedStudent} onValueChange={loadStudentDetail}>
      <SelectTrigger data-testid="mon-student-select" className="w-full max-w-sm">
        <SelectValue placeholder="Изберете ученик" />
      </SelectTrigger>
      <SelectContent>
        {students.map((s) => (
          <SelectItem key={s.user_id} value={s.user_id}>{s.name} ({s.username})</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8EDE2' }}>
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ClipboardCheck className="h-6 w-6 text-indigo-600" />
            <div>
              <h1 className="text-lg sm:text-2xl font-bold text-slate-900">БНУ Азбукарче</h1>
              <p className="text-xs sm:text-sm text-slate-600">МОН Инспектор: {user?.username}</p>
            </div>
          </div>
          <Button onClick={handleLogout} variant="outline" size="sm" data-testid="logout-btn" className="flex items-center gap-2">
            <LogOut className="h-4 w-4" /> Изход
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="mb-4">
          <p className="text-sm text-slate-600">
            Достъп само за четене. Виждате единствено учениците в онлайн обучение, отбелязани в списъка на МОН
            ({students.length} {students.length === 1 ? 'ученик' : 'ученици'}).
          </p>
        </div>

        <Tabs defaultValue="students" className="space-y-6">
          <TabsList data-testid="mon-tabs" className="flex flex-wrap w-full h-auto gap-1 p-1 bg-slate-100 rounded-lg">
            <TabsTrigger value="students" data-testid="mon-students-tab" className="flex-1 flex items-center gap-1 py-2">
              <Users className="h-4 w-4" /> Ученици
            </TabsTrigger>
            <TabsTrigger value="attendance" data-testid="mon-attendance-tab" className="flex-1 flex items-center gap-1 py-2">
              <ClipboardList className="h-4 w-4" /> Присъствия
            </TabsTrigger>
            <TabsTrigger value="competencies" data-testid="mon-competencies-tab" className="flex-1 flex items-center gap-1 py-2">
              <Award className="h-4 w-4" /> Компетентности
            </TabsTrigger>
            <TabsTrigger value="lessons" data-testid="mon-lessons-tab" className="flex-1 flex items-center gap-1 py-2">
              <BookOpen className="h-4 w-4" /> Учебни записи
            </TabsTrigger>
          </TabsList>

          <TabsContent value="students">
            <div className="rounded-md border border-slate-200 overflow-x-auto bg-white">
              <table className="w-full min-w-[500px]">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Ученик</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Потребител</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Класове</th>
                  </tr>
                </thead>
                <tbody>
                  {students.length === 0 ? (
                    <tr><td colSpan={3} className="px-4 py-8 text-center text-slate-500">Няма ученици в списъка на МОН</td></tr>
                  ) : students.map((s) => (
                    <tr key={s.user_id} data-testid="mon-student-row" className="border-t border-slate-100">
                      <td className="px-4 py-3 text-sm font-medium text-slate-900">{s.name}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">@{s.username}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{(s.class_names || []).join(', ') || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="attendance">
            <div className="space-y-4">
              <StudentPicker />
              {!selectedStudent ? (
                <Card className="p-8 text-center text-slate-500">Изберете ученик, за да видите присъствията</Card>
              ) : attendance.length === 0 ? (
                <Card className="p-8 text-center text-slate-500">Няма записи за присъствия</Card>
              ) : (
                <div className="rounded-md border border-slate-200 overflow-x-auto bg-white">
                  <table className="w-full min-w-[400px]">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Дата</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-slate-500">Статус</th>
                      </tr>
                    </thead>
                    <tbody>
                      {attendance.map((a, i) => (
                        <tr key={i} className="border-t border-slate-100">
                          <td className="px-4 py-3 text-sm text-slate-700">{new Date(a.date).toLocaleDateString('bg-BG')}</td>
                          <td className="px-4 py-3">
                            <Badge className={a.status === 'present' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                              {a.status === 'present' ? 'Присъствал' : 'Отсъствал'}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="competencies">
            <div className="space-y-4">
              <StudentPicker />
              {!selectedStudent ? (
                <Card className="p-8 text-center text-slate-500">Изберете ученик, за да видите компетентностите</Card>
              ) : competencies.length === 0 ? (
                <Card className="p-8 text-center text-slate-500">Няма оценени компетентности</Card>
              ) : (
                <div className="space-y-2">
                  {competencies.map((c, i) => (
                    <Card key={i} className="p-3 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-slate-900">{c.item_text || '-'}</p>
                        <p className="text-xs text-slate-500">{c.group_name}{c.subject ? ` · ${c.subject}` : ''}</p>
                      </div>
                      <Badge className={statusColor(c.status)}>{statusLabel(c.status)}</Badge>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="lessons">
            {lessons.length === 0 ? (
              <Card className="p-8 text-center text-slate-500">Няма учебни записи</Card>
            ) : (
              <div className="space-y-3">
                {lessons.map((l) => (
                  <Card key={l.lesson_id} data-testid="mon-lesson-card" className="p-4">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <Badge variant="outline" className="text-xs">{new Date(l.date).toLocaleDateString('bg-BG')}</Badge>
                      {l.class_name && <Badge className="text-xs bg-blue-100 text-blue-700">{l.class_name}</Badge>}
                      <span className="text-xs text-slate-500">Учител: {l.teacher_name}</span>
                    </div>
                    <div className="text-sm text-slate-800 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(l.description || '') }} />
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
